import {
  DesignatedTruckCarModelOptions,
  DesignatedTruckFeatureOptions,
  DesignatedTruckFloorSpecificationOptions,
  DesignatedTruckKlassOptions,
  DesignatedTruckLoadingPlatformHeightOptions,
  DesignatedTruckLoadingPlatformLengthOptions,
  DesignatedTruckLoadingPlatformWidthOptions,
} from 'src/entities/orderEntity';
import { ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import {
  ShipmentDetailFormData,
  ShipmentOrderResponseEntity,
  ShipmentOrderWaypointTimeEntity,
} from 'src/entities/ShipmentOrderResponse.entity';
import {
  mergeConstraintSelectionsByMasterOrder,
} from 'src/utils/laneBasicInfoMerge';
import { getEarliestDateFormatted } from 'src/utils/shipmentLegRunItems';
import {
  processWaypointTimes,
  validateLoadingUnloadingDateRangeForWaypointTimes,
} from 'src/utils/shipmentOrderWaypointTimes';

/** レーン基本情報タブの編集ドラフト（全伝票に同一内容を適用） */
export type LaneBasicInfoDraft = {
  loadingWaypointTimes: ShipmentOrderWaypointTimeEntity[];
  unloadingWaypointTimes: ShipmentOrderWaypointTimeEntity[];
  loadingStayingMinutes: number | '';
  unloadingStayingMinutes: number | '';
  designatedTruckKlasses: ShipmentOrderResponseEntity['designatedTruckKlasses'];
  designatedTruckCarModels: ShipmentOrderResponseEntity['designatedTruckCarModels'];
  designatedTruckLoadingPlatformHeights: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformHeights'];
  designatedTruckLoadingPlatformWidths: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformWidths'];
  designatedTruckLoadingPlatformLengths: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformLengths'];
  designatedTruckFloorSpecifications: ShipmentOrderResponseEntity['designatedTruckFloorSpecifications'];
  designatedTruckFeatures: ShipmentOrderResponseEntity['designatedTruckFeatures'];
  allowedTruckIds: number[];
  deniedDriverIds: number[];
};

export const cloneWaypointTimes = (
  times: ShipmentOrderWaypointTimeEntity[] | undefined,
): ShipmentOrderWaypointTimeEntity[] =>
  JSON.parse(JSON.stringify(times ?? [])) as ShipmentOrderWaypointTimeEntity[];

export const createLaneBasicInfoDraftFromDetail = (
  detail: ShipmentOrderLaneDetail,
): LaneBasicInfoDraft | null => {
  const orders = detail.shipmentOrders;
  if (!orders?.length) return null;

  const first = orders[0];

  return {
    loadingWaypointTimes: cloneWaypointTimes(
      first.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes,
    ),
    unloadingWaypointTimes: cloneWaypointTimes(
      first.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes,
    ),
    loadingStayingMinutes: first.shipmentOrderLoadingWaypoint.stayingMinutes ?? '',
    unloadingStayingMinutes: first.shipmentOrderUnloadingWaypoint.stayingMinutes ?? '',
    designatedTruckKlasses: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckKlasses,
      DesignatedTruckKlassOptions,
    ),
    designatedTruckCarModels: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckCarModels,
      DesignatedTruckCarModelOptions,
    ),
    designatedTruckLoadingPlatformHeights: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformHeights,
      DesignatedTruckLoadingPlatformHeightOptions,
    ),
    designatedTruckLoadingPlatformWidths: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformWidths,
      DesignatedTruckLoadingPlatformWidthOptions,
    ),
    designatedTruckLoadingPlatformLengths: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformLengths,
      DesignatedTruckLoadingPlatformLengthOptions,
    ),
    designatedTruckFloorSpecifications: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFloorSpecifications,
      DesignatedTruckFloorSpecificationOptions,
    ),
    designatedTruckFeatures: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFeatures,
      DesignatedTruckFeatureOptions,
    ),
    allowedTruckIds: [...new Set(orders.flatMap((o) => o.allowedTruckIds ?? []))],
    deniedDriverIds: [...new Set(orders.flatMap((o) => o.deniedDriverIds ?? []))],
  };
};

/**
 * 1 件の伝票にドラフトを適用した PATCH 用ペイロードを組み立てる。
 * 時間枠は伝票ごとの元 ID を保持したまま processWaypointTimes する。
 */
export const buildShipmentDetailFormDataForLaneSave = (
  order: ShipmentOrderResponseEntity,
  draft: LaneBasicInfoDraft,
): ShipmentDetailFormData => {
  const processedLoading = processWaypointTimes(
    order.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [],
    draft.loadingWaypointTimes,
  );
  const processedUnloading = processWaypointTimes(
    order.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [],
    draft.unloadingWaypointTimes,
  );

  const loadingMin = draft.loadingStayingMinutes === '' ? '' : Number(draft.loadingStayingMinutes);
  const unloadingMin = draft.unloadingStayingMinutes === '' ? '' : Number(draft.unloadingStayingMinutes);

  const validLoadingTimes = processedLoading.filter((t) => !t._destroy && t.startAt);
  const validUnloadingTimes = processedUnloading.filter((t) => !t._destroy && t.startAt);

  return {
    ...order,
    // issue #283: 備考・任意項目は伝票ごとに異なる固有情報のため、レーンドラフトの値で
    // 全伝票を上書きしない。...order 由来の各伝票自身の元値をそのまま保持する。
    designatedTruckKlasses: draft.designatedTruckKlasses,
    designatedTruckCarModels: draft.designatedTruckCarModels,
    designatedTruckLoadingPlatformHeights: draft.designatedTruckLoadingPlatformHeights,
    designatedTruckLoadingPlatformWidths: draft.designatedTruckLoadingPlatformWidths,
    designatedTruckLoadingPlatformLengths: draft.designatedTruckLoadingPlatformLengths,
    designatedTruckFloorSpecifications: draft.designatedTruckFloorSpecifications,
    designatedTruckFeatures: draft.designatedTruckFeatures,
    allowedTruckIds: draft.allowedTruckIds,
    deniedDriverIds: draft.deniedDriverIds,
    shipmentOrderLoadingWaypoint: {
      ...order.shipmentOrderLoadingWaypoint,
      stayingMinutes: draft.loadingStayingMinutes,
      stayingSeconds: loadingMin === '' ? '' : loadingMin * 60,
      shipmentOrderWaypointLoadingTimes: processedLoading,
    },
    shipmentOrderUnloadingWaypoint: {
      ...order.shipmentOrderUnloadingWaypoint,
      stayingMinutes: draft.unloadingStayingMinutes,
      stayingSeconds: unloadingMin === '' ? '' : unloadingMin * 60,
      shipmentOrderWaypointUnloadingTimes: processedUnloading,
    },
    loadingOn: getEarliestDateFormatted(validLoadingTimes),
    unloadingOn: getEarliestDateFormatted(validUnloadingTimes),
    loadingTimeSlot: 'カスタム',
    unloadingTimeSlot: 'カスタム',
    sectionTypes: order.sectionTypes ?? [],
  };
};

export const validateLaneBasicInfoDraft = (
  orders: ShipmentOrderResponseEntity[],
  draft: LaneBasicInfoDraft,
): boolean => {
  for (const order of orders) {
    const pl = processWaypointTimes(
      order.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [],
      draft.loadingWaypointTimes,
    );
    const pu = processWaypointTimes(
      order.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [],
      draft.unloadingWaypointTimes,
    );
    if (!validateLoadingUnloadingDateRangeForWaypointTimes(pl, pu)) {
      return false;
    }
  }
  return true;
};
